import { DismissButton } from '@shared/components/dismiss-button';
import { useOptionalRef } from '@shared/hooks';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { ForwardedRef, forwardRef, useState } from 'react';
import { Text } from '../text';

export type DismissibleBannerProps = StyleProps & {
  variant?: 'default' | 'success' | 'error' | 'warning';
  title: string;
  onDismiss?: () => void;
};

const ROOT = makeRootClassName('DismissibleBanner');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_PROPS = {
  variant: 'default',
} as const;

const DismissibleBannerComponent = (
  props: DismissibleBannerProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const domRef = useOptionalRef(ref);
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    p.onDismiss?.();
  };

  return (
    <AnimatePresence>
      {!isDismissed && (
        <motion.div
          ref={domRef}
          className={clsx(`${ROOT} variant-${p.variant}`, p.className)}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleY: 0 }}
          transition={{
            duration: 0.2,
            ease: [0.4, 0.0, 0.2, 1],
          }}
          style={{
            transformOrigin: 'top',
            overflow: 'hidden',
          }}
        >
          <div className={el`content`}>
            <Text type="body-xs" className={el`title`}>
              {p.title}
            </Text>
          </div>
          <DismissButton
            onPress={handleDismiss}
            className={clsx(el`dismiss-button`, `variant-${p.variant}`)}
            size="sm"
            variant="circled"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const DismissibleBanner = forwardRef<HTMLDivElement, DismissibleBannerProps>(
  DismissibleBannerComponent
);

export default DismissibleBanner;
